import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";

import { ActivatedRoute, Params } from "@angular/router";
import { AccountMarketplace, Marketplace, ProductGroup } from "@front/m19-api-client";
import { Catalog, ProductGroupEx } from "@front/m19-models";
import { AccountSelectionService, AsinService, ProductGroupService } from "@front/m19-services";
import { IButtonDirective, IIconComponent, ToasterService } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { NavBreadcrumbDirective } from "@m19-board/containers/page-layout/nav-breadcrumb.directive";
import { LoadingService } from "@m19-board/services/loading.service";
import { AsinSelectionComponent } from "@m19-board/shared/asin-selection/asin-selection.component";
import { EditableTextComponent } from "@m19-board/shared/editable-text/editable-text.component";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest } from "rxjs";
import { map, switchMap } from "rxjs/operators";

@UntilDestroy()
@Component({
  selector: "app-product-group-page",
  templateUrl: "./product-group-page.component.html",
  imports: [
    TranslocoDirective,
    IButtonDirective,
    AsinSelectionComponent,
    EditableTextComponent,
    NavBreadcrumbDirective,
    IIconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductGroupPageComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private productGroupService = inject(ProductGroupService);
  private asinService = inject(AsinService);
  private accountSelectionService = inject(AccountSelectionService);
  private toastrService = inject(ToasterService);
  private translocoService = inject(TranslocoService);
  private loadingService = inject(LoadingService);

  productGroup = signal<ProductGroupEx | null | undefined>(undefined);
  alreadySelectedProducts = signal<string[] | undefined>(undefined);
  newAsins = signal<string[]>([]);
  marketplace?: Marketplace;
  accountId?: string;
  isReadOnly = toSignal(this.accountSelectionService.readOnlyMode$, { requireSync: true });
  editMode = signal(false);
  private catalog?: string[];

  ngOnInit() {
    this.loadingService.loadingOn();
    this.accountSelectionService.singleAccountMarketplaceSelection$
      .pipe(untilDestroyed(this))
      .subscribe((am: AccountMarketplace) => {
        this.marketplace = am.marketplace;
        this.accountId = am.accountId;
      });
    combineLatest<[Params, ProductGroupEx[]]>([
      this.route.params,
      this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
        switchMap((am) => this.productGroupService.getProductGroups(am.accountId, am.marketplace)),
      ),
    ])
      .pipe(
        map(([params, productGroups]) => {
          const id = Number(params["id"]);
          return productGroups.find((pg) => pg.productGroupId == id);
        }),
      )
      .subscribe((productGroup: ProductGroupEx | undefined) => {
        this.loadingService.loadingOff();
        this.productGroup.set(productGroup);
        this.alreadySelectedProducts.set(productGroup?.items);
      });

    this.accountSelectionService.singleAccountMarketplaceSelection$
      .pipe(
        untilDestroyed(this),
        switchMap((am: AccountMarketplace) => this.asinService.getCatalog(am.accountId, am.marketplace)),
      )
      .subscribe((catalog: Catalog) => {
        this.catalog = [...catalog.childAsins.values()];
      });
  }

  changeName(newname: string) {
    this.productGroupService
      .updateProductGroupName(this.accountId!, this.marketplace!, this.productGroup()!.productGroupId, newname)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.toastrService.success(this.translocoService.translate("product-group.product_group_name_updated"));
        },
        error: (err) => {
          this.toastrService.error(
            err,
            this.translocoService.translate("product-group-page.error_updating_product_group_name"),
          );
        },
      });
  }

  updateProductGroupProducts(newAsins: string[]) {
    const toAdd = newAsins.filter((a) => !this.alreadySelectedProducts()?.includes(a));
    const toRemove = this.alreadySelectedProducts()?.filter((a) => !newAsins.includes(a));
    if (toAdd && toAdd.length > 0) {
      this.addAsinsArrayToProductGroup(toAdd);
    }
    if (toRemove && toRemove.length > 0) {
      this.deleteAsinArrayFromProductGroup(toRemove);
    }
  }

  addAsinsArrayToProductGroup(asins: string[]) {
    this.productGroupService
      .addAsinsToProductGroup(
        this.accountId!,
        this.marketplace!,
        (this.productGroup() as ProductGroupEx).productGroupId,
        asins,
        this.catalog!,
      )
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.translocoService.translate("product-group-page.asins_added_to_product_group"),
          );
          this.newAsins.set([]);
          this.editMode.set(false);
        },
        error: (err) => {
          this.toastrService.error(
            err,
            this.translocoService.translate("product-group-page.error_adding_asins_to_product_group"),
          );
        },
      });
  }

  deleteAsinArrayFromProductGroup(asins: string[]) {
    if (this.isReadOnly()) {
      return;
    }
    this.productGroupService
      .deleteAsinsFromProductGroup(
        this.accountId!,
        this.marketplace!,
        (this.productGroup() as ProductGroupEx).productGroupId,
        asins,
      )
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.translocoService.translate("product-group-page.asins_removed_from_product_group"),
          );
          this.newAsins.set([]);
          this.editMode.set(false);
        },
        error: (err) => {
          this.toastrService.error(
            err,
            this.translocoService.translate("product-group-page.error_removing_asins_from_product_group"),
          );
        },
      });
  }
}
